/**
 * useMediaQuery Hook
 *
 * Responsive breakpoint detection hook for progressive disclosure patterns.
 * Returns boolean indicating if the media query matches.
 *
 * Usage:
 * const isMobile = useMediaQuery('(max-width: 768px)');
 * const isDesktop = useMediaQuery('(min-width: 1024px)');
 */

import { useState, useEffect } from 'react';

export function useMediaQuery(query: string): boolean {
  // Initialise synchronously from the real match so the very first paint already
  // reflects the correct breakpoint. Initialising to `false` then flipping in the
  // post-mount effect caused a Cumulative Layout Shift: e.g. ECSLayout's <main>
  // margin-left jumped 0 → sidebar-width on every desktop load (the whole content
  // area slid right, ~0.15 CLS — the dominant global-shell shift). SSR-safe:
  // window/matchMedia are guarded; jsdom's matchMedia stub returns matches=false.
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    // Check if window is defined (SSR safety)
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(query);

    // Set initial value
    setMatches(mediaQuery.matches);

    // Create event listener
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [query]);

  return matches;
}

// Predefined breakpoint hooks for common use cases
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)');
}

export function useIsTablet(): boolean {
  return useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
}

export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)');
}

// Specific breakpoint for multi-stage 3-column layout
export function useIsLargeDesktop(): boolean {
  return useMediaQuery('(min-width: 1280px)');
}
